import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";

/* The words a person reads, for the slug the engine recorded.
 *
 * The engine stores a campaign by its `name`, which is the slug, because that
 * is what its scripts take. `label` is what a person wrote. Migration 087 made
 * the Campaigns list read the label and three other places kept printing the
 * slug: the run name on a campaign record, the enrichment batch, and the Jobs
 * screen, which was fixed on its own and is now folded in here.
 *
 * Archived campaigns are included on purpose. A job and a frozen run are
 * history, their campaign may well have been archived since, and one this map
 * could not name would fall back to the slug again.
 */
export function useCampaignLabels(token: string) {
  return useQuery({
    queryKey: ["campaign-labels"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("criteria_set").select("name,label").limit(500);
      if (error) throw new Error(apiErrorMessage(error));
      const map = new Map<string, string>();
      for (const r of (data ?? []) as { name?: string | null; label?: string | null }[]) {
        if (r.name && r.label) map.set(r.name, r.label);
      }
      return map;
    },
  });
}

/** A campaign slug as words, or the slug when nothing has named it. */
export function campaignWords(
  slug: string | null | undefined,
  labels: Map<string, string> | undefined,
): string {
  if (!slug) return "";
  return labels?.get(slug) ?? slug;
}

/* A frozen run is named "<slug>, <date>", because the engine builds it from
   the criteria set name. Only the slug half is replaced, so a run somebody
   froze under their own label keeps it. */
export function runWords(
  runName: string | null | undefined,
  slug: string | null | undefined,
  labels: Map<string, string> | undefined,
): string {
  if (!runName) return "";
  if (!slug || !runName.startsWith(slug)) return runName;
  const words = labels?.get(slug);
  return words ? words + runName.slice(slug.length) : runName;
}

/* An enrichment batch a campaign raised is named "campaign:<slug>:<date>".
   Every other batch is a name somebody typed and is left alone. */
export function batchWords(
  batch: string | null | undefined,
  labels: Map<string, string> | undefined,
): string {
  if (!batch) return "";
  const [prefix, slug, when] = batch.split(":");
  if (!slug || !when || prefix !== "campaign") return batch;
  const words = labels?.get(slug);
  return words ? `${words}, ${when}` : batch;
}
